import { HeaderTestIds } from '@trello/test-ids';
import React, {
  Suspense,
  FunctionComponent,
  useCallback,
  useState,
} from 'react';
import { AddIcon } from '@trello/nachos/icons/add';
import { Noun, Verb, trackUe } from '@trello/analytics';
import HeaderButton from './button';
import { usePopover, Popover, PopoverScreen } from '@trello/nachos/popover';
import { forNamespace, forTemplate } from 'app/common/lib/util/i18n';
import { TeamType } from '../../modules/state/models/teams';
import { useLazyComponent } from '@trello/use-lazy-component';
import { Analytics, getScreenFromUrl } from '@trello/atlassian-analytics';
import Auth from 'app/scripts/db/auth.coffee';

const viewTitle = forNamespace('view title');

enum Screen {
  Menu,
  CreateEnterpriseTeam,
  SelectTemplate,
  CreateBoardFromTemplate,
}

const screenAnalyticsNames: {
  [key: string]: 'createFromTemplateInlineDialog' | 'useTemplateInlineDialog';
} = {
  [Screen.SelectTemplate]: 'createFromTemplateInlineDialog',
  [Screen.CreateBoardFromTemplate]: 'useTemplateInlineDialog',
};

export const CreateMenuButton: FunctionComponent = () => {
  const { triggerRef, toggle, hide, popoverProps, push, pop } = usePopover<
    HTMLButtonElement
  >({
    initialScreen: Screen.Menu,
  });

  const [selectedTemplate, setSelectedTemplate] = useState({
    id: '',
    name: '',
  });

  const onClick = useCallback(() => {
    trackUe({
      category: Noun.HEADER,
      verb: Verb.OPENS,
      directObj: Noun.ADD_MENU,
      method: 'by clicking header add menu button',
    });

    Analytics.sendClickedButtonEvent({
      buttonName: 'createMenuHeaderButton',
      source: getScreenFromUrl(),
    });

    toggle();
  }, [toggle]);

  const onHide = useCallback(() => {
    if (
      popoverProps.currentScreen &&
      screenAnalyticsNames[popoverProps.currentScreen]
    ) {
      Analytics.sendClosedComponentEvent({
        componentType: 'inlineDialog',
        componentName: screenAnalyticsNames[popoverProps.currentScreen],
        source: 'createMenuScreen',
      });
    }

    hide();
  }, [popoverProps, hide]);

  const onBack = useCallback(() => {
    if (
      popoverProps.currentScreen &&
      screenAnalyticsNames[popoverProps.currentScreen]
    ) {
      Analytics.sendClickedButtonEvent({
        buttonName: 'inlineDialogBackButton',
        source: screenAnalyticsNames[popoverProps.currentScreen],
      });
    }

    if (popoverProps.onBack) {
      popoverProps.onBack();
    } else {
      pop();
    }
  }, [popoverProps, pop]);

  const HeaderCreateMenuPopover = useLazyComponent(() =>
    import(
      /* webpackChunkName: "create-menu-popover" */ 'app/gamma/src/components/popovers/header-create-menu'
    ),
  );

  const CreateBoardFromTemplatePopover = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "create-board-from-template-popover" */ 'app/src/components/CreateBoardFromTemplate'
      ),
    {
      preload: popoverProps.isVisible,
      namedImport: 'CreateBoardFromTemplatePopover',
    },
  );

  const CreateBoardPopover = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "create-board-popover" */ 'app/src/components/CreateBoardFromTemplate'
      ),
    {
      preload: popoverProps.isVisible,
      namedImport: 'CreateBoardPopover',
    },
  );

  const CreateTeamPopover = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "create-team-popover" */ 'app/gamma/src/components/popovers/create-team'
      ),
    {
      preload: popoverProps.isVisible,
      namedImport: 'CreateTeamPopover',
    },
  );

  return (
    <>
      <HeaderButton
        icon={<AddIcon color="light" />}
        onClick={onClick}
        ref={triggerRef}
        testId={HeaderTestIds.CreateMenuButton}
        ariaLabel={forTemplate('header_user')('create-board-or-team')}
      />
      <Popover
        {...popoverProps}
        title={viewTitle('create')}
        testId={HeaderTestIds.CreateMenuPopover}
        onHide={onHide}
        onBack={onBack}
      >
        <Suspense fallback={null}>
          <PopoverScreen id={Screen.Menu} noHorizontalPadding>
            <HeaderCreateMenuPopover
              onClickCreateEnterpriseTeam={() =>
                push(Screen.CreateEnterpriseTeam)
              }
              onClickCreateBoardFromTemplate={() => {
                !Auth.me().isDismissed('start-with-a-template') &&
                  Auth.me().dismiss('start-with-a-template');

                push(Screen.SelectTemplate);
              }}
              onCreateTeamOverlayShown={() => hide()}
              onCreateBoardOverlayShown={() => hide()}
            />
          </PopoverScreen>
          <PopoverScreen
            id={Screen.SelectTemplate}
            title={viewTitle('create-from-template')}
          >
            <CreateBoardFromTemplatePopover
              hide={() => hide()}
              onSelectTemplate={({
                id,
                name,
              }: {
                id: string;
                name: string;
              }) => {
                Analytics.sendUIEvent({
                  action: 'clicked',
                  actionSubject: 'menuItem',
                  actionSubjectId: 'createFromTemplateMenuItem',
                  source: 'createFromTemplateInlineDialog',
                  containers: {
                    board: {
                      id,
                    },
                  },
                });

                setSelectedTemplate({
                  id,
                  name,
                });
                push(Screen.CreateBoardFromTemplate);
              }}
            />
          </PopoverScreen>
          <PopoverScreen
            id={Screen.CreateBoardFromTemplate}
            title={selectedTemplate.name}
          >
            <CreateBoardPopover boardId={selectedTemplate.id} />
          </PopoverScreen>
          <PopoverScreen
            id={Screen.CreateEnterpriseTeam}
            title={viewTitle('create organization')}
          >
            <CreateTeamPopover teamType={TeamType.Enterprise} />
          </PopoverScreen>
        </Suspense>
      </Popover>
    </>
  );
};
